// static/table_engine_src/layout/autosize.js
import { escCss } from "../util/dom.js";

export function installAutosize(ctx) {
  // ctx requirements:
  //  - ctx.table, ctx.thead, ctx.cols, ctx.hiddenCols
  //  - ctx.getRowEls()
  //  - ctx.autoWidthMode ("off" | "all" | "unsetOnly")
  //  - ctx.autoWidthKeys()  (optional; else we build a default)
  //  - ctx.notifyRendered scheduling not needed here

  let _autoWidthPending = false;
  let _autoWidthWantAll = false;
  let _autoWidthWanted = new Set();
  const _valueHookByEl = new WeakMap();
  const _restoreHookByEl = new WeakMap();

  function findAccessorDescriptor(obj, prop) {
    let p = obj;
    while (p) {
      const d = Object.getOwnPropertyDescriptor(p, prop);
      if (d) return d;
      p = Object.getPrototypeOf(p);
    }
    return null;
  }

  function installElementValueHook(el, prop) {
    if (!el || !prop) return false;

    const existingMeta = _restoreHookByEl.get(el);
    if (existingMeta && existingMeta.props && existingMeta.props.has(prop)) return true;

    const baseDesc = findAccessorDescriptor(el, prop);
    if (!baseDesc || typeof baseDesc.get !== "function" || typeof baseDesc.set !== "function") {
      return false;
    }

    const ownDesc = Object.getOwnPropertyDescriptor(el, prop) || null;

    try {
      Object.defineProperty(el, prop, {
        get() {
          return baseDesc.get.call(this);
        },
        set(v) {
          baseDesc.set.call(this, v);
          try {
            const cb = _valueHookByEl.get(this);
            if (typeof cb === "function" && document.activeElement !== this) cb();
          } catch {}
        },
        configurable: true,
        enumerable: !!baseDesc.enumerable,
      });
    } catch {
      return false;
    }

    if (!existingMeta) {
      const restoreByProp = new Map();
      restoreByProp.set(prop, ownDesc);

      const meta = {
        props: new Set([prop]),
        restoreByProp,
        restore: () => {
          for (const [p, prevDesc] of restoreByProp.entries()) {
            try {
              if (prevDesc) {
                Object.defineProperty(el, p, prevDesc);
              } else {
                delete el[p];
              }
            } catch {}
          }
        },
      };
      _restoreHookByEl.set(el, meta);
      ctx.addCleanup?.(() => {
        const m = _restoreHookByEl.get(el);
        if (!m) return;
        try {
          m.restore?.();
        } catch {}
        _restoreHookByEl.delete(el);
        _valueHookByEl.delete(el);
      });
      return true;
    }

    existingMeta.props.add(prop);
    existingMeta.restoreByProp?.set?.(prop, ownDesc);
    return true;
  }

  function eligibleKeysNow() {
    return new Set((typeof ctx.autoWidthKeys === "function" ? ctx.autoWidthKeys() : defaultAutoWidthKeys()));
  }

  function queueWanted(keys = null, eligible = null) {
    if (Array.isArray(keys) && keys.length) {
      const allow = eligible || eligibleKeysNow();
      keys.forEach((k) => {
        if (allow.has(k)) _autoWidthWanted.add(k);
      });
      return;
    }
    _autoWidthWantAll = true;
  }

  function flushWanted() {
    const allow = eligibleKeysNow();
    const runKeys = _autoWidthWantAll
      ? [...allow]
      : [..._autoWidthWanted].filter((k) => allow.has(k));

    _autoWidthWantAll = false;
    _autoWidthWanted.clear();
    return runKeys;
  }

  function defaultAutoWidthKeys() {
    // fallback if you don’t pass ctx.autoWidthKeys
    return ctx.cols
      .filter((c) => {
        if (!c?.key) return false;
        if (ctx.hiddenCols?.has(c.key)) return false;
        if (c.kind === "actions" || c.kind === "rowHandle") return false;
        if (c.autoWidth === false) return false;

        if (ctx.autoWidthMode === "all") return true;
        return c.width === undefined || c.width === null || c.width === "";
      })
      .map((c) => c.key);
  }

  function scheduleAutoWidth(keys = null) {
    if (ctx.autoWidthMode === "off") return;

    const eligible = eligibleKeysNow();
    const wanted =
      Array.isArray(keys) && keys.length
        ? keys.filter((k) => eligible.has(k))
        : [...eligible];

    if (!wanted.length) return;
    queueWanted(Array.isArray(keys) && keys.length ? wanted : null, eligible);
    if (_autoWidthPending) return;
    _autoWidthPending = true;

    requestAnimationFrame(() => {
      _autoWidthPending = false;
      const runKeys = flushWanted();
      if (runKeys.length) autosizeColumns(runKeys);
    });
  }

  function hookProgrammaticValueAutosize(inputEl, colKey) {
    if (!inputEl || !colKey) return;

    try {
      _valueHookByEl.set(inputEl, () => scheduleAutoWidth([colKey]));

      const tag = String(inputEl.tagName || "").toUpperCase();
      installElementValueHook(inputEl, "value");
      if (tag === "INPUT") installElementValueHook(inputEl, "valueAsNumber");
      if (tag === "SELECT") installElementValueHook(inputEl, "selectedIndex");
    } catch {}
  }

  function autosizeColumns(keys = null) {
    const table = ctx.table;
    const thead = ctx.thead;
    if (!table || !thead) return;

    const cg = table.querySelector("colgroup");
    if (!cg) return;

    const headRow = thead.querySelector("tr");
    if (!headRow) return;

    const footTotalRow = table.querySelector("tfoot tr.te-total-row");
    const targetKeys = keys && keys.length ? keys : ctx.cols.map((c) => c.key);
    const bodyRows = (ctx.getRenderedRowEls?.() || ctx.getRowEls?.() || []);
    const colIdxByKey = new Map();
    ctx.cols.forEach((c, idx) => {
      if (c?.key != null) colIdxByKey.set(c.key, idx);
    });

    let measurer = autosizeColumns._measurer;
    if (!measurer) {
      measurer = document.createElement("span");
      measurer.className = "te-measure";
      measurer.style.position = "fixed";
      measurer.style.left = "-9999px";
      measurer.style.top = "-9999px";
      measurer.style.visibility = "hidden";
      measurer.style.whiteSpace = "pre";
      measurer.style.padding = "0";
      measurer.style.margin = "0";
      measurer.style.border = "0";
      measurer.style.pointerEvents = "none";
      document.body.appendChild(measurer);
      autosizeColumns._measurer = measurer;
    }

    const px = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };

    const boxX = (el) => {
      if (!el) return 0;
      const cs = getComputedStyle(el);
      return (
        px(cs.paddingLeft) +
        px(cs.paddingRight) +
        px(cs.borderLeftWidth) +
        px(cs.borderRightWidth)
      );
    };

    const fontOf = (el) => {
      if (!el) return "";
      const cs = getComputedStyle(el);
      return cs.font || `${cs.fontStyle} ${cs.fontVariant} ${cs.fontWeight} ${cs.fontSize}/${cs.lineHeight} ${cs.fontFamily}`;
    };

    const measureText = (txt, font) => {
      measurer.style.font = font || "";
      measurer.textContent = txt == null ? "" : String(txt);
      const r = measurer.getBoundingClientRect();
      return r.width || 0;
    };

    const cellText = (td) => {
      if (!td) return "";
      const inp = td.querySelector("input,textarea");
      if (inp) return inp.value || inp.placeholder || "";
      const sel = td.querySelector("select");
      if (sel) {
        const opt = sel.selectedOptions && sel.selectedOptions[0];
        return opt ? (opt.textContent || "") : "";
      }
      const out = td.querySelector(".cell-output,span");
      if (out) return out.textContent || "";
      return td.textContent || "";
    };

    const prevLayout = table.style.tableLayout;
    table.style.tableLayout = "auto";
    let didChange = false;

    targetKeys.forEach((k) => {
      if (ctx.hiddenCols?.has(k)) return;

      const idx = colIdxByKey.get(k);
      if (!Number.isFinite(idx) || idx < 0) return;

      const th = headRow.children[idx];
      const colEl = cg.querySelector(`col[data-col-key="${escCss(k)}"]`);
      if (!colEl || !th) return;

      let maxW = 0;

      // header
      {
        const t = (th.textContent || "").trim();
        const w = measureText(t, fontOf(th)) + boxX(th) + 8;
        maxW = Math.max(maxW, w);
      }

      // body
      bodyRows.forEach((tr) => {
        const td = tr.children[idx];
        if (!td || td.style.display === "none") return;

        const t = cellText(td);
        const wText = measureText(t, fontOf(td));
        const inner = td.querySelector("input,textarea,select,.cell-output,span");
        const w = wText + boxX(td) + boxX(inner) + 8;
        maxW = Math.max(maxW, w);
      });

      // footer
      if (footTotalRow) {
        const td = footTotalRow.children[idx];
        if (td && td.style.display !== "none") {
          const t = cellText(td);
          const wText = measureText(t, fontOf(td));
          const inner = td.querySelector("input,textarea,select,.cell-output,span");
          const w = wText + boxX(td) + boxX(inner) + 8;
          maxW = Math.max(maxW, w);
        }
      }

      const w = Math.min(Math.max(44, Math.ceil(maxW)), 800);
      const col = ctx.cols[idx] || null;
      if (col) {
        const prevColWidth =
          Number.isFinite(col.width)
            ? Number(col.width)
            : (typeof col.width === "string" ? parseFloat(col.width) : NaN);
        if (!(Number.isFinite(prevColWidth) && Math.abs(prevColWidth - w) < 0.5)) {
          col.width = w;
          didChange = true;
        }
      }

      colEl.style.width = `${w}px`;
    });

    table.style.tableLayout = prevLayout || "";
    if (didChange) ctx.persistLayoutProfile?.();
  }

  Object.assign(ctx, {
    autosizeColumns,
    scheduleAutoWidth,
    hookProgrammaticValueAutosize,
  });
}
